import { Suspense, lazy, useMemo, Component, ReactNode } from 'react';
import { MODULE_REGISTRY } from './module-registry';
import { withAppBasePath } from '../nav';

type ModuleKey = keyof typeof MODULE_REGISTRY;

const lazyModules: Partial<Record<ModuleKey, ReturnType<typeof lazy>>> = {};

function getLazy(key: ModuleKey) {
  if (!lazyModules[key]) {
    lazyModules[key] = lazy(async () => {
      try {
        const mod = (await MODULE_REGISTRY[key].lazy()) as any;
        const Comp = mod.ModuleApp || mod.default;
        if (!Comp) {
          throw new Error(
            `Module ${key} package loaded but did not export ModuleApp or default. ` +
              `See packages/host-contract — modules must export <ModuleApp basePath={...}/>.`
          );
        }
        return { default: Comp };
      } catch (err) {
        const msg = String(err);
        return { default: () => <NotInstalled moduleKey={key} error={msg} /> };
      }
    });
  }
  return lazyModules[key]!;
}

export function ModuleHost({ moduleKey }: { moduleKey: ModuleKey }) {
  const Comp = useMemo(() => getLazy(moduleKey), [moduleKey]);
  const basePath = withAppBasePath(`/m/${moduleKey}`);
  return (
    <ErrorBoundary fallback={(err) => <NotInstalled moduleKey={moduleKey} error={err} />}>
      <Suspense fallback={<div className="loading">Loading {MODULE_REGISTRY[moduleKey].name}…</div>}>
        <Comp basePath={basePath} />
      </Suspense>
    </ErrorBoundary>
  );
}

function NotInstalled({ moduleKey, error }: { moduleKey: string; error: string }) {
  return (
    <div className="module-not-installed">
      <h2>{MODULE_REGISTRY[moduleKey as ModuleKey].name} — not yet built</h2>
      <p>
        The <code>@admin-portal/{moduleKey}-gui</code> package isn't installed yet.
      </p>
      <p>
        The module agent for <strong>{moduleKey}</strong> needs to ship their GUI in the sibling folder{' '}
        <code>../{moduleKey}_admin_web/</code>, then run <code>pnpm install</code> in this repo.
      </p>
      <details>
        <summary>Error</summary>
        <pre>{error}</pre>
      </details>
    </div>
  );
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: (err: string) => ReactNode;
}
class ErrorBoundary extends Component<ErrorBoundaryProps, { error: string | null }> {
  state = { error: null as string | null };
  static getDerivedStateFromError(error: Error) {
    return { error: String(error) };
  }
  render() {
    return this.state.error ? this.props.fallback(this.state.error) : this.props.children;
  }
}
